<script lang="ts">
  import { onMount } from 'svelte';
  import { currentUser, isAuthenticated } from '$lib/stores/auth';
  import { api } from '$lib/api/client';
  import type { User, Page, Card } from '$lib/api/types';
  import Sidebar from '$components/layout/Sidebar.svelte';
  import CardGrid from '$components/layout/CardGrid.svelte';
  import Toolbar from '$components/layout/Toolbar.svelte';
  import NotFound from './NotFound.svelte';

  // Route params passed by svelte-routing
  export let params: { username?: string } = {};

  // Check if embedded (hide scrollbar but allow scroll)
  let isEmbed = false;
  onMount(() => {
    const urlParams = new URLSearchParams(window.location.search);
    isEmbed = urlParams.get('embed') === 'true';
  });

  let user: User | null = null;
  let page: Page | null = null;
  let cards: Card[] = [];
  let loading = true;
  let error: string | null = null;
  let cardGrid: CardGrid;
  let draggingSidebarLink: Card | null = null;
  let draggingGridCard: Card | null = null;

  // Is this the current user's own page?
  $: isOwnPage = $isAuthenticated && $currentUser?.username === params.username;

  // Reload data when username changes
  $: if (params.username) {
    loadPageData();
  }

  async function loadPageData() {
    if (!params.username) {
      error = 'not_found';
      loading = false;
      return;
    }

    loading = true;
    error = null;

    try {
      const username = params.username;

      // Load user, page, and cards in parallel
      const [userData, pageData, cardsData] = await Promise.all([
        api.users.get(username),
        api.pages.get(username),
        api.cards.list(username),
      ]);

      user = userData;
      page = pageData;
      cards = cardsData.cards;
    } catch (err: any) {
      console.error('Failed to load page:', err);
      if (err.code === 'NOT_FOUND') {
        error = 'not_found';
      } else {
        error = err.message || 'Failed to load page';
      }
    } finally {
      loading = false;
    }
  }

  function handleCardCreated(event: CustomEvent<Card>) {
    cards = [...cards, event.detail];
  }

  function handleCardUpdated(event: CustomEvent<Card>) {
    cards = cards.map((c) => (c.id === event.detail.id ? event.detail : c));
  }

  function handleCardDeleted(event: CustomEvent<string>) {
    cards = cards.filter((c) => c.id !== event.detail);
    // Trigger compact after delete (with small delay to let state update)
    setTimeout(() => cardGrid?.triggerCompact(), 50);
  }

  async function handleCardMoved(event: CustomEvent<{
    cardId: string;
    x: number;
    y: number;
    pushedCards: Array<{ cardId: string; y: number }>;
  }>) {
    const { cardId, x, y, pushedCards } = event.detail;

    // Optimistically update local state
    cards = cards.map(c => {
      if (c.id === cardId) {
        return { ...c, x, y };
      }
      const pushed = pushedCards.find(p => p.cardId === c.id);
      if (pushed) {
        return { ...c, y: pushed.y };
      }
      return c;
    });

    // Update via API
    try {
      const updated = await api.cards.update(cardId, { x, y });
      cards = cards.map(c => c.id === updated.id ? updated : c);

      // Update pushed cards
      for (const pushed of pushedCards) {
        const updatedPushed = await api.cards.update(pushed.cardId, { y: pushed.y });
        cards = cards.map(c => c.id === updatedPushed.id ? updatedPushed : c);
      }

      // Trigger compact after move
      setTimeout(() => cardGrid?.triggerCompact(), 50);
    } catch (err) {
      console.error('Failed to move card:', err);
      // Reload on error to get correct state
      loadPageData();
    }
  }

  async function handleCardResized(event: CustomEvent<{
    cardId: string;
    width: number;
    height: number;
    pushedCards: Array<{ cardId: string; y: number }>;
  }>) {
    const { cardId, width, height, pushedCards } = event.detail;

    // Optimistically update local state
    cards = cards.map(c => {
      if (c.id === cardId) {
        return { ...c, width, height };
      }
      const pushed = pushedCards.find(p => p.cardId === c.id);
      if (pushed) {
        return { ...c, y: pushed.y };
      }
      return c;
    });

    // Update via API
    try {
      // Update resized card
      const updatedMain = await api.cards.update(cardId, { width, height });
      cards = cards.map(c => c.id === updatedMain.id ? updatedMain : c);

      // Update pushed cards
      for (const pushed of pushedCards) {
        const updated = await api.cards.update(pushed.cardId, { y: pushed.y });
        cards = cards.map(c => c.id === updated.id ? updated : c);
      }

      // Trigger compact after resize
      setTimeout(() => cardGrid?.triggerCompact(), 50);
    } catch (err) {
      console.error('Failed to resize card:', err);
      // Reload on error to get correct state
      loadPageData();
    }
  }

  async function handleCardsCompacted(event: CustomEvent<Array<{ cardId: string; y: number }>>) {
    const movedCards = event.detail;
    if (movedCards.length === 0) return;

    // Optimistically update local state
    cards = cards.map(c => {
      const moved = movedCards.find(m => m.cardId === c.id);
      if (moved) {
        return { ...c, y: moved.y };
      }
      return c;
    });

    // Update via API
    try {
      for (const moved of movedCards) {
        const updated = await api.cards.update(moved.cardId, { y: moved.y });
        cards = cards.map(c => c.id === updated.id ? updated : c);
      }
    } catch (err) {
      console.error('Failed to compact cards:', err);
      loadPageData();
    }
  }

  async function handleSidebarLinkDropped(event: CustomEvent<{
    cardId: string;
    x: number;
    y: number;
    pushedCards: Array<{ cardId: string; y: number }>;
  }>) {
    const { cardId, x, y, pushedCards } = event.detail;

    // Find the card and update it
    const card = cards.find(c => c.id === cardId);
    if (!card) return;

    // Optimistically update local state
    cards = cards.map(c => {
      if (c.id === cardId) {
        return { ...c, x, y, data: { ...c.data, showInSidebar: false } };
      }
      const pushed = pushedCards.find(p => p.cardId === c.id);
      if (pushed) {
        return { ...c, y: pushed.y };
      }
      return c;
    });

    // Update via API
    try {
      const updated = await api.cards.update(cardId, {
        x,
        y,
        data: { ...card.data, showInSidebar: false }
      });
      cards = cards.map(c => c.id === updated.id ? updated : c);

      // Update pushed cards
      for (const pushed of pushedCards) {
        const updatedPushed = await api.cards.update(pushed.cardId, { y: pushed.y });
        cards = cards.map(c => c.id === updatedPushed.id ? updatedPushed : c);
      }

      // Trigger compact after dropping from sidebar
      setTimeout(() => cardGrid?.triggerCompact(), 50);
    } catch (err) {
      console.error('Failed to move sidebar link:', err);
      loadPageData();
    }
  }

  async function handleCardDroppedToSidebar(event: CustomEvent<string>) {
    const cardId = event.detail;

    // Find the card
    const card = cards.find(c => c.id === cardId);
    if (!card || card.type !== 'link') return; // Only link cards can go to sidebar

    // Optimistically update local state
    cards = cards.map(c => {
      if (c.id === cardId) {
        return { ...c, data: { ...c.data, showInSidebar: true } };
      }
      return c;
    });

    // Update via API
    try {
      const updated = await api.cards.update(cardId, {
        data: { ...card.data, showInSidebar: true }
      });
      cards = cards.map(c => c.id === updated.id ? updated : c);

      // Trigger compact after moving to sidebar
      setTimeout(() => cardGrid?.triggerCompact(), 50);
    } catch (err) {
      console.error('Failed to move card to sidebar:', err);
      loadPageData();
    }
  }

  function handleUserUpdated(event: CustomEvent<User>) {
    user = event.detail;
  }

  function handlePageUpdated(event: CustomEvent<Page>) {
    page = event.detail;
  }

  // Get theme class
  $: themeClass = page ? `theme-${page.theme}` : 'theme-light';

  // Page title and favicon
  $: pageTitle = user ? (user.displayName || user.username) : 'Velf.me';
  $: faviconUrl = user?.iconUrl || '/favicon.svg';

  // Filter sidebar links (link cards with showInSidebar flag)
  $: sidebarLinks = cards.filter(c => c.type === 'link' && c.data?.showInSidebar);

  // Filter out sidebar links from grid display
  $: gridCards = cards.filter(c => !(c.type === 'link' && c.data?.showInSidebar));
</script>

<svelte:head>
  <title>{pageTitle}</title>
  <link rel="icon" href={faviconUrl} />
</svelte:head>

{#if loading}
  <div class="min-h-screen flex items-center justify-center">
    <div class="text-gray-500">読み込み中...</div>
  </div>
{:else if error === 'not_found'}
  <NotFound />
{:else if error}
  <div class="min-h-screen flex items-center justify-center">
    <div class="text-red-500">{error}</div>
  </div>
{:else if user && page}
  <div class={`min-h-screen ${themeClass} ${isEmbed ? 'embed-mode' : ''}`} style:--accent-color={page.accentColor || undefined} style:background-color="var(--bg-color)">
    <div class="max-w-[1260px] mx-auto px-6 min-[1280px]:px-0">
      <div class="flex flex-col min-[1280px]:flex-row min-[1280px]:min-h-screen">
        <!-- Profile Sidebar -->
        <Sidebar
          {user}
          {isOwnPage}
          {sidebarLinks}
          {draggingGridCard}
          on:updated={handleUserUpdated}
          on:linkDragStart={(e) => draggingSidebarLink = e.detail}
          on:linkDragEnd={() => draggingSidebarLink = null}
          on:cardDroppedToSidebar={handleCardDroppedToSidebar}
        />

        <!-- Card Grid -->
        <main class="flex-1 min-[1280px]:p-6 min-[1280px]:pt-12 pb-24">
          <CardGrid
            cards={gridCards}
            allCards={cards}
            {isOwnPage}
            {draggingSidebarLink}
            on:cardUpdated={handleCardUpdated}
            on:cardDeleted={handleCardDeleted}
            on:cardMoved={handleCardMoved}
            on:cardResized={handleCardResized}
            on:cardsCompacted={handleCardsCompacted}
            on:sidebarLinkDropped={handleSidebarLinkDropped}
            on:cardDragStart={(e) => draggingGridCard = e.detail}
            on:cardDragEnd={() => draggingGridCard = null}
            bind:this={cardGrid}
          />
        </main>
      </div>
    </div>

    <!-- Edit Toolbar (only shown for own page) -->
    {#if isOwnPage}
      <Toolbar
        {page}
        {cards}
        on:cardCreated={handleCardCreated}
        on:pageUpdated={handlePageUpdated}
      />
    {/if}
  </div>
{/if}

<style>
  :global(.embed-mode) {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    padding-top: 48px; /* Space for Dynamic Island */
  }
  :global(.embed-mode::-webkit-scrollbar) {
    display: none; /* Chrome, Safari, Opera */
  }
</style>
